import { computed } from 'vue'

import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { IconArrowDownRight } from '@ds/icons/IconArrowDownRight'
import { IconBoltFilled } from '@ds/icons/IconBoltFilled'
import { addWeeks, isMonday, set, startOfWeek, subWeeks } from 'date-fns'

import translations from '../components/ProductCardDeal.translations'
import type { Granularity } from '../models/product'

export function useProductCardDeal(
  deal?: Deal,
  granularity?: Granularity,
  withDeal = true,
) {
  const i18n = useI18n()
  const experiments = useExperiments()
  const locale = useI18nLocale()

  const isFlashSalesEnabled = experiments['experiment.flashSales'] === 'enabled'
  const isSellerFlashSalesEnabled =
    experiments['experiment.sellerFlashSales'] === 'with'

  const dealContent = computed(() => {
    if (!withDeal || !deal) return null

    if (isFlashSalesEnabled && deal.type === 'flash_sales')
      return {
        label: i18n(translations.flashSalesTag, {
          price: i18n.price(deal.discount, {
            trailingZeroDisplay: 'stripIfInteger',
          }),
        }),
        icon: IconBoltFilled,
      }

    if (
      isSellerFlashSalesEnabled &&
      granularity === 'backbox' &&
      deal.type === 'seller_flash_sales' &&
      deal.percentage?.value
    ) {
      const now = new Date()
      const mondayThisWeek = set(startOfWeek(now, { weekStartsOn: 1 }), {
        hours: 12,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      })

      // If it's Monday before 12pm, the current week hasn't started yet
      const startDate =
        isMonday(now) && now.getHours() < 12
          ? subWeeks(mondayThisWeek, 1)
          : mondayThisWeek

      const endDate = addWeeks(startDate, 1)

      return {
        label: i18n(translations.sellerFlashSalesTag, {
          percentage: `${deal.percentage.value}%`,
        }),
        icon: IconArrowDownRight,
        tooltipText: i18n(translations.sellerFlashSalesTagTooltip, {
          start_date: startDate.toLocaleDateString(locale, {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
          }),
          end_date: endDate.toLocaleDateString(locale, {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
          }),
          percentage: deal.percentage.value,
          lowest_price: i18n.price(deal.percentage.minimum_price, {
            trailingZeroDisplay: 'stripIfInteger',
          }),
        }),
      }
    }

    return null
  })

  return {
    dealContent,
  }
}
